import React from 'react';
import { Box, Typography } from '@mui/material';
import { BenchmarkRun, DispositionMatrixResponse, RunResultsSummary } from '../../../../services/benchmarkService';
import { PanelSkeleton, StatBlock, TierPill } from '../../BenchmarkPrimitives';
import { count, percent } from '../../BenchmarkVocabulary';
import { EvidenceChip, QualifiedBadge, WithheldNotice } from '../../BenchmarkQualifiers';
import { evidenceLabel, isIndicative } from '../../benchmarkEvidence';
import { PANEL_FAMILIES, WithheldIndex, qualificationsFor, suppressionReasons } from '../../withheldMetrics';
import { dsVar } from '../../../../theme/tokens';

interface ResultsSummaryStripProps {
    run: BenchmarkRun;
    summary: RunResultsSummary | null;
    matrix: DispositionMatrixResponse | null;
    withheld: WithheldIndex;
    loading?: boolean;
}

const tileSx = {
    p: 2.5, borderRadius: '12px', border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('surface'), minWidth: 0
};

/**
 * The sample at a glance: how many cases were scored, how often the platform's
 * tier matched the user's own, and what the investigated outcomes were in each
 * platform tier. Always as counts with the number of cases behind them — a
 * supplied sample is usually small, and a percentage alone overstates it.
 */
const ResultsSummaryStrip: React.FC<ResultsSummaryStripProps> = ({ run, summary, matrix, withheld, loading }) => {
    if (loading && !summary && !matrix) return <PanelSkeleton height={180} />;

    const caseCount = run.predictionCount;
    const grouped = summary && summary.alertCount > summary.caseCount;

    const exact = matrix?.concordance?.exactAgreement ?? null;
    const withinOne = matrix?.concordance?.withinOneTier ?? null;
    const concordanceEvidence = exact ? { sampleSize: exact.sampleSize, confidence: exact.confidence } : null;
    const concordanceWithheld = suppressionReasons(withheld, PANEL_FAMILIES.concordance);

    const tiersWithCases = (matrix?.agentTiers ?? []).filter((t) => t.totalCount > 0);

    return (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr 1.6fr' } }}>
            <Box sx={tileSx}>
                <StatBlock
                    label="Cases scored"
                    value={count(caseCount)}
                    detail={grouped
                        ? `From ${count(summary!.alertCount)} alerts — alerts for the same customer are scored as one case.`
                        : summary ? `One case per alert you supplied.` : undefined}
                />
            </Box>

            <Box sx={tileSx}>
                <Box className="flex flex-wrap items-center gap-1.5 mb-1">
                    <EvidenceChip evidence={concordanceEvidence} />
                    <QualifiedBadge qualifications={qualificationsFor(withheld, PANEL_FAMILIES.concordance)} />
                </Box>
                {concordanceWithheld.length > 0 ? (
                    <WithheldNotice reasons={concordanceWithheld} compact />
                ) : (
                    <StatBlock
                        label="Agreement with your tiers"
                        value={exact ? `${percent(exact.value, 0)} exact` : 'Not computed'}
                        definition="How often the platform gave a case the same tier as your own, once your tiers are mapped onto the platform's scale. Within one tier counts a neighbouring tier as agreeing."
                        indicative={exact ? isIndicative(concordanceEvidence) : false}
                        detail={[
                            withinOne ? `${percent(withinOne.value, 0)} within one tier` : null,
                            exact ? evidenceLabel(concordanceEvidence) : 'Your tiers have no approved mapping, or no case could be compared.'
                        ].filter(Boolean).join(' · ')}
                    />
                )}
            </Box>

            <Box sx={tileSx}>
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontWeight: 500, mb: 1 }}>
                    Outcome by platform tier
                </Typography>
                {tiersWithCases.length === 0 ? (
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>No scored cases.</Typography>
                ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                        <Box component="thead">
                            <Box component="tr">
                                {['Platform tier', 'Cases', 'Confirmed', 'No issue', 'SARs'].map((h, i) => (
                                    <Box
                                        key={h}
                                        component="th"
                                        sx={{
                                            textAlign: i === 0 ? 'left' : 'right', pb: 0.75, fontSize: '0.6875rem',
                                            fontWeight: 600, color: dsVar('fg-muted'), textTransform: 'uppercase', letterSpacing: '0.04em'
                                        }}
                                    >
                                        {h}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {tiersWithCases.map((t) => (
                                <Box component="tr" key={t.tier} sx={{ borderTop: `1px solid ${dsVar('line')}` }}>
                                    <Box component="td" sx={{ py: 0.75 }}><TierPill code={t.tier} /></Box>
                                    {[t.totalCount, t.truePositives, t.falsePositives, t.sarsFiled].map((n, i) => (
                                        <Box key={i} component="td" sx={{ py: 0.75, textAlign: 'right', color: dsVar('fg-soft') }}>
                                            {count(n)}
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ResultsSummaryStrip;
